<script setup>
import { computed, ref, watch, useSlots } from 'vue';

import WootEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  editorKey: { type: String, default: '' },
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  focusOnMount: { type: Boolean, default: false },
  maxLength: { type: Number, default: 200 },
  showCharacterCount: { type: Boolean, default: true },
  disabled: { type: Boolean, default: false },
  message: { type: String, default: '' },
  messageType: {
    type: String,
    default: 'info',
    validator: value => ['info', 'error', 'success'].includes(value),
  },
  enableVariables: { type: Boolean, default: false },
  enableCannedResponses: { type: Boolean, default: true },
  enableCaptainTools: { type: Boolean, default: false },
  signature: { type: String, default: '' },
  allowSignature: { type: Boolean, default: false },
  sendWithSignature: { type: Boolean, default: false },
  channelType: { type: String, default: '' },
  medium: { type: String, default: '' },
  minHeight: { type: [Number, String], default: 4 },
  isFormatMode: { type: Boolean, default: false },
  enableSuggestions: { type: Boolean, default: true },
  showImageResizeToolbar: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:modelValue',
  'executeCopilotAction',
  'blur',
  'focus',
]);

const slots = useSlots();

const isFocused = ref(false);

const characterCount = computed(() => props.modelValue.length);

const messageClass = computed(() => {
  switch (props.messageType) {
    case 'error':
      return 'text-n-ruby-9 dark:text-n-ruby-9';
    case 'success':
      return 'text-n-teal-10 dark:text-n-teal-10';
    default:
      return 'text-n-slate-11 dark:text-n-slate-11';
  }
});

const handleInput = value => {
  if (!props.disabled) {
    emit('update:modelValue', value);
  }
};

const handleFocus = () => {
  if (!props.disabled) {
    isFocused.value = true;
    emit('focus');
  }
};

const handleBlur = () => {
  if (!props.disabled) {
    isFocused.value = false;
    emit('blur');
  }
};

watch(
  () => props.modelValue,
  newValue => {
    if (props.maxLength && props.showCharacterCount && !slots.actions) {
      if (characterCount.value >= props.maxLength) {
        emit('update:modelValue', newValue.slice(0, props.maxLength));
      }
    }
  }
);
</script>

<template>
  <div class="flex flex-col min-w-0 gap-1">
    <label v-if="label" class="mb-0.5 text-sm font-medium text-n-slate-12">
      {{ label }}
    </label>
    <div
      class="flex flex-col w-full gap-2 px-4 py-4 transition-all duration-300 ease-in-out border rounded-2xl editor-wrapper bg-muted/30"
      :class="[
        {
          'cursor-not-allowed opacity-50 pointer-events-none !bg-muted/50 border-border':
            disabled,
          'border-primary ring-2 ring-primary/20 bg-background shadow-sm':
            isFocused,
          'hover:border-border/80 border-border shadow-sm':
            !isFocused && messageType !== 'error',
          'border-destructive/50 bg-destructive/5 hover:border-destructive':
            messageType === 'error' && !isFocused,
        },
      ]"
    >
      <WootEditor
        v-bind="$attrs"
        :editor-id="editorKey"
        :model-value="modelValue"
        :placeholder="placeholder"
        :focus-on-mount="focusOnMount"
        :disabled="disabled"
        :enable-variables="enableVariables"
        :enable-canned-responses="enableCannedResponses"
        :enable-captain-tools="enableCaptainTools"
        :signature="signature"
        :allow-signature="allowSignature"
        :send-with-signature="sendWithSignature"
        :channel-type="channelType"
        :medium="medium"
        :min-height="minHeight"
        :is-format-mode="isFormatMode"
        :enable-suggestions="enableSuggestions"
        :show-image-resize-toolbar="showImageResizeToolbar"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @execute-copilot-action="
          (...args) => emit('executeCopilotAction', ...args)
        "
      />
      <div
        v-if="showCharacterCount || slots.actions"
        class="flex items-center justify-end h-6 mt-1"
      >
        <span
          v-if="showCharacterCount && !slots.actions"
          class="text-[11px] font-bold tabular-nums text-muted-foreground bg-muted px-2 py-0.5 rounded-full"
          dir="ltr"
        >
          {{ characterCount }} / {{ maxLength }}
        </span>
        <slot v-else name="actions" />
      </div>
    </div>
    <p
      v-if="message"
      class="min-w-0 mt-1 mb-0 text-xs truncate transition-all duration-300 ease-in-out px-1 font-medium"
      :class="messageClass"
    >
      {{ message }}
    </p>
  </div>
</template>

<style lang="scss" scoped>
.editor-wrapper {
  :deep(.ProseMirror-menubar-wrapper) {
    .ProseMirror.ProseMirror-woot-style {
      @apply text-sm leading-relaxed;

      p {
        @apply first:mt-0 !important;
      }

      .empty-node {
        @apply m-0 !important;

        &::before {
          @apply text-muted-foreground/60 font-medium !important;
        }
      }
    }

    .ProseMirror-menubar {
      width: fit-content !important;
      position: relative !important;
      top: unset !important;
      @apply ltr:left-[-0.188rem] rtl:right-[-0.188rem] mb-2 !important;
      @apply bg-background/50 backdrop-blur-sm rounded-lg p-1 border border-border/50 !important;
    }
  }
}
</style>
